<template>
  <div class="detail-disposisi-page">
    <div class="page-header">
      <div class="header-title-wrapper">
        <h1>Detail Disposisi Masuk</h1>
        <p class="header-subtitle">
          {{ data?.suratMasuk?.nomorSurat }} - {{ data?.suratMasuk?.pengirim }}
        </p>
      </div>
      <div class="header-tags">
        <a-tag
          v-if="data?.statusDisposisi === 'Didisposisikan'"
          color="orange"
          style="border-radius: 50px;"
        >
          <strong>{{ data?.statusDisposisi }}</strong>
        </a-tag>
        <a-tag
          v-else-if="data?.statusDisposisi === 'Selesai Disposisi'"
          color="green"
          style="border-radius: 50px;"
        >
          <strong>{{ data?.statusDisposisi }}</strong>
        </a-tag>
        <a-tag v-else-if="data?.statusDisposisi" color="default" style="border-radius: 50px;">
          <strong>{{ data?.statusDisposisi }}</strong>
        </a-tag>

        <a-tag
          v-if="data?.statusDisposisi !== 'Selesai Disposisi'"
          :color="getSubstatus().color"
          style="border-radius: 50px;"
        >
          <strong>{{ getSubstatus().label }}</strong>
        </a-tag>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <a-spin size="large" tip="Memuat data disposisi..." />
    </div>

    <!-- Main Content -->
    <div v-else-if="data" class="content-container">
      <!-- Detail Surat Section -->
      <h4 class="section-title">Detail Surat</h4>
      <div class="detail-grid">
        <div class="detail-row">
          <span class="detail-label">Pengarsip Surat</span>
          <span class="detail-value"
            >{{ data.suratMasuk?.creator?.name }} ({{ data.suratMasuk?.creator?.jabatan }})</span
          >
        </div>
        <div class="detail-row">
          <span class="detail-label">Waktu Diregistrasikan</span>
          <span class="detail-value">{{ formatDateTime(data.suratMasuk?.createdAt) }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Waktu Penerimaan Surat</span>
          <span class="detail-value">{{ formatDateTime(data.suratMasuk?.waktuPenerimaan) }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Nomor Surat</span>
          <span class="detail-value">{{ data.suratMasuk?.nomorSurat || '-' }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Tanggal Surat</span>
          <span class="detail-value">{{ formatDate(data.suratMasuk?.tanggalSurat) }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Pengirim</span>
          <span class="detail-value">{{ data.suratMasuk?.pengirim || '-' }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Lampiran</span>
          <span class="detail-value">{{ data.suratMasuk?.lampiran || 'Tidak ada' }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Perihal</span>
          <span class="detail-value">{{ data.suratMasuk?.perihal || '-' }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Keterangan</span>
          <span class="detail-value">{{ data.suratMasuk?.keterangan || 'Tidak ada' }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Surat Masuk</span>
          <span class="detail-value">
            <div v-if="data.suratMasuk?.pathToFile" class="file-link">
              <PaperClipOutlined />
              <span class="file-link-name">{{ getFileName(data.suratMasuk?.pathToFile) }}</span>
              <span
                class="view-icon-link"
                @click.stop.prevent="previewSuratMasuk"
                title="Preview Surat"
                style="cursor: pointer; margin-left: 8px;"
              >
                <EyeOutlined class="view-icon" />
              </span>
              <a
                :href="`/surat-masuk/update/${data.suratMasuk?.id}`"
                target="_blank"
                style="margin-left: 4px;"
                title="Buka detail surat masuk"
              >
                <ExportOutlined style="color: #e10f28;" />
              </a>
            </div>
            <span v-else>-</span>
          </span>
        </div>
      </div>

      <a-divider />

      <!-- Disposisi Section -->
      <h4 class="section-title">Disposisi</h4>
      <div class="detail-grid">
        <div class="detail-row">
          <span class="detail-label">Disposisi Kepada</span>
          <span class="detail-value">{{ getDisposisiKepada() }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Pembuat Disposisi</span>
          <span class="detail-value"
            >{{ data.suratMasuk?.creator?.name }} ({{ data.suratMasuk?.creator?.jabatan }})</span
          >
        </div>
        <div class="detail-row">
          <span class="detail-label">Waktu Disposisi Dibuat</span>
          <span class="detail-value">{{ formatDateTime(data.updatedAt) }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Perihal Instruksi</span>
          <span class="detail-value">{{ data.judulTask || data.perihalInstruksi || '-' }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Instruksi Disposisi</span>
          <span class="detail-value">{{
            data.deskripsiTask || data.instruksiDisposisi || 'Tidak ada'
          }}</span>
        </div>
        <div v-if="data.deadlineTask" class="detail-row">
          <span class="detail-label">Deadline Task</span>
          <span class="detail-value">{{ formatDate(data.deadlineTask) }}</span>
        </div>
      </div>

      <!-- Komentar Disposisi Section -->
      <div class="comment">
        <div class="title-container">
          <h4 class="sub-section-title" style="margin: 0;">Komentar Disposisi</h4>
        </div>
        <div class="comment-section">
          <!-- Empty State -->
          <div v-if="comments.length === 0 && !commentsLoading" class="comment-empty-state">
            <MessageOutlined class="empty-icon" />
            <p style="width: 100%;">
              Anda dapat melaporkan hasil disposisi dengan mengirimkan komentar, memilih surat
              keluar yang sudah dibuat di E-Office, atau unggah file terkait melalui fitur ini
            </p>
          </div>

          <!-- Comments List -->
          <div v-else class="comments-list">
            <div
              v-for="comment in comments"
              :key="comment.id"
              class="comment-item"
              :class="{ 'own-comment': isCommentOwner(comment) }"
            >
              <div class="comment-bubble">
                <div class="comment-user-name">
                  {{ isCommentOwner(comment) ? 'Anda' : comment.user?.name || 'Pengguna' }}
                  <div class="comment-time">
                    {{ formatCommentTime(comment.createdAt) }}
                  </div>
                </div>
                <div class="comment-content">
                  <!-- Attached Files (shown first if exists) -->
                  <div
                    v-if="comment.attachments && comment.attachments.length > 0"
                    class="comment-attachment"
                  >
                    <div
                      v-for="(attachment, idx) in comment.attachments"
                      :key="idx"
                      style="display: flex; gap: 8px;"
                    >
                      <!-- Surat Keluar attachment -->
                      <template v-if="isSuratKeluarAttachment(attachment)">
                        <div class="attachment-link">
                          <PaperClipOutlined class="attachment-icon" />
                          {{ attachment.name || 'Surat Keluar' }}
                        </div>
                        <span
                          class="view-icon-link"
                          @click.stop.prevent="navigateToSuratKeluar(attachment)"
                          title="Preview Surat Keluar"
                          style="cursor: pointer;"
                        >
                          <EyeOutlined class="view-icon" />
                        </span>
                        <a
                          :href="`/surat-keluar/${attachment.suratKeluarId}`"
                          target="_blank"
                          class="view-icon-link"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            class="view-icon"
                          >
                            <path
                              d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"
                            ></path>
                            <polyline points="15 3 21 3 21 9"></polyline>
                            <line x1="10" y1="14" x2="21" y2="3"></line>
                          </svg>
                        </a>
                      </template>
                      <!-- Regular file attachment -->
                      <template v-else>
                        <div class="attachment-link">
                          <PaperClipOutlined class="attachment-icon" />
                          {{ getFileName(attachment) || 'Draft Surat Rekomendasi' }}
                        </div>
                        <a :href="getFileUrl(attachment)" target="_blank">
                          <EyeOutlined class="view-icon" />
                        </a>
                      </template>
                    </div>
                  </div>
                  <!-- Linked Surat Keluar (legacy support) -->
                  <div v-if="comment.suratKeluar" class="comment-surat-keluar">
                    <a :href="`/surat-keluar/${comment.suratKeluar.id}`" class="surat-keluar-link">
                      <PaperClipOutlined /> {{ comment.suratKeluar.nomorSurat }}
                      <EyeOutlined class="view-icon" />
                      <ExportOutlined class="export-icon" />
                    </a>
                  </div>
                  <p v-if="comment.content">{{ comment.content }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Comment Input Section -->
          <div class="comment-input-section">
            <div class="comment-tabs">
              <button
                @click="openSuratKeluarModal"
                class="comment-action-button"
                :disabled="statusDisposisi === 'Selesai Disposisi'"
              >
                <SearchOutlined /> PILIH SURAT KELUAR
              </button>
              <a-upload
                v-model:file-list="commentFileList"
                :before-upload="beforeUpload"
                :show-upload-list="false"
                :multiple="true"
                :disabled="statusDisposisi === 'Selesai Disposisi'"
                class="upload-button-wrapper"
              >
                <button
                  class="comment-action-button"
                  :disabled="statusDisposisi === 'Selesai Disposisi'"
                >
                  <UploadOutlined /> UNGGAH FILE
                </button>
              </a-upload>
            </div>

            <!-- Attachment Preview (above textarea) -->
            <div v-if="commentFileList.length" class="attachment-preview">
              <div v-for="(file, index) in commentFileList" :key="index" class="attachment-item">
                <PaperClipOutlined class="file-icon" />
                <span class="attachment-name">{{ file.name || file.originFileObj?.name }}</span>
                <button
                  type="button"
                  class="delete-button"
                  @click.prevent="removeFile(index)"
                  title="Hapus file"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <polyline points="3 6 5 6 21 6"></polyline>
                    <path
                      d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
                    ></path>
                    <line x1="10" y1="11" x2="10" y2="17"></line>
                    <line x1="14" y1="11" x2="14" y2="17"></line>
                  </svg>
                </button>
              </div>
            </div>

            <!-- Comment Text Area -->
            <div class="comment-textarea-wrapper">
              <div class="custom-comment-input-row">
                <a-textarea
                  v-model:value="newComment"
                  placeholder="Ketik komentar..."
                  :rows="2"
                  :disabled="submittingComment || statusDisposisi === 'Selesai Disposisi'"
                  class="custom-comment-textarea"
                  style="min-height: 80px;"
                />
                <a-button
                  class="custom-send-btn"
                  :loading="submittingComment"
                  :disabled="
                    (!newComment.trim() && !commentFileList.length && !selectedSuratKeluar) ||
                      statusDisposisi === 'Selesai Disposisi'
                  "
                  @click="submitComment"
                >
                  <ArrowUpOutlined />
                  <span class="send-label">KIRIM</span>
                </a-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Error State -->
    <div v-else class="error-container">
      <a-result
        status="error"
        title="Gagal Memuat Data"
        sub-title="Data disposisi tidak ditemukan atau terjadi kesalahan"
      >
        <template #extra>
          <a-button type="primary" @click="goBack">Kembali</a-button>
        </template>
      </a-result>
    </div>

    <!-- Surat Keluar Selection Modal -->
    <a-modal
      v-model:visible="suratKeluarModalVisible"
      title="Pilih surat keluar"
      :footer="null"
      width="800px"
      centered
      class="surat-keluar-modal"
    >
      <p class="modal-subtitle">
        Silakan pilih surat keluar yang ingin dikirimkan pada komentar disposisi
      </p>

      <!-- Search Input -->
      <a-input
        v-model:value="suratKeluarSearch"
        placeholder="Cari nomor surat, jenis surat, atau perihal"
        size="large"
        class="surat-keluar-search"
        allow-clear
      >
        <template #prefix>
          <SearchOutlined style="color: #bfbfbf" />
        </template>
      </a-input>

      <!-- Surat Keluar Table -->
      <div class="surat-keluar-table-container">
        <a-spin :spinning="suratKeluarLoading">
          <a-table
            :data-source="filteredSuratKeluar"
            :columns="suratKeluarColumns"
            :row-selection="{
              selectedRowKeys: selectedSuratKeluarKeys,
              onChange: onSuratKeluarSelectionChange,
            }"
            :pagination="false"
            :scroll="{ x: 820, y: 400 }"
            row-key="id"
            size="small"
            class="surat-keluar-table"
          >
            <template #bodyCell="{ column, record }">
              <template v-if="column.key === 'nomorSurat'">
                <span class="surat-nomor">{{ record.nomorSurat }}</span>
              </template>
              <template v-else-if="column.key === 'tanggalSurat'">
                {{ formatDate(record.tanggalSurat) }}
              </template>
              <template v-else-if="column.key === 'jenisSurat'">
                {{ record.jenisSurat }}
              </template>
              <template v-else-if="column.key === 'namaKegiatan'">
                {{ record.namaKegiatan || record.perihal || '-' }}
              </template>
              <template v-else-if="column.key === 'pengaju'">
                {{ record.creator?.name || '-' }}
              </template>
            </template>
          </a-table>
        </a-spin>
      </div>

      <!-- Modal Actions -->
      <div class="surat-keluar-modal-actions">
        <button
          class="confirm-button"
          :disabled="selectedSuratKeluarKeys.length === 0"
          @click="confirmSuratKeluarSelection"
        >
          <ArrowUpOutlined /> LAMPIRKAN SURAT
        </button>
        <button class="remove-cancel-button remove-cancel-secondary" @click="closeSuratKeluarModal">
          KEMBALI
        </button>
      </div>
    </a-modal>

    <!-- PDF Preview Modal -->
    <PDFPreviewModal
      v-model:visible="pdfPreviewVisible"
      :title="pdfPreviewTitle"
      :pdf-path="pdfPreviewPath"
      :pdf-list="pdfPreviewList"
    />
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, computed, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { notification } from 'ant-design-vue'
import {
  LeftOutlined,
  PaperClipOutlined,
  EyeOutlined,
  ExportOutlined,
  MessageOutlined,
  SearchOutlined,
  UploadOutlined,
  ArrowUpOutlined,
} from '@ant-design/icons-vue'
import { disposisiAPI, uploadFilesAPI, suratKeluarAPI } from '@/services/axios/perki'
import { PDFPreviewModal } from '@/components/SuratKeluar'
import { useStore } from 'vuex'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import 'dayjs/locale/id'

dayjs.extend(relativeTime)
dayjs.locale('id')

export default {
  name: 'DetailDisposisiMasuk',
  components: {
    PaperClipOutlined,
    EyeOutlined,
    ExportOutlined,
    MessageOutlined,
    SearchOutlined,
    UploadOutlined,
    ArrowUpOutlined,
    PDFPreviewModal,
  },
  setup() {
    const router = useRouter()
    const route = useRoute()
    const store = useStore()
    const loading = ref(true)
    const data = ref(null)

    // Comment state
    const comments = ref([])
    const commentsLoading = ref(false)
    const newComment = ref('')
    const submittingComment = ref(false)
    const activeTab = ref('surat-keluar')
    const commentFileList = ref([])
    const selectedSuratKeluar = ref(null)
    const suratKeluarList = ref([])
    const statusDisposisi = ref(null)

    const pollingInterval = ref(null)

    // Surat Keluar Modal state
    const suratKeluarModalVisible = ref(false)
    const suratKeluarLoading = ref(false)
    const suratKeluarSearch = ref('')
    const selectedSuratKeluarKeys = ref([])
    const userGroupId = localStorage.getItem('perki_group_id')

    // PDF Preview Modal state
    const pdfPreviewVisible = ref(false)
    const pdfPreviewTitle = ref('')
    const pdfPreviewPath = ref('')
    const pdfPreviewList = ref([])

    const FILES_URL = process.env.VUE_APP_PERKI_URL || 'http://localhost:3000'

    // Get current user from store (store.state.user has id, name, role, email directly)
    const currentUser = computed(() => {
      const perkiUser = localStorage.getItem('perki_user')
      return perkiUser ? JSON.parse(perkiUser) : {}
    })

    // Get substatus based on comments
    const getSubstatus = () => {
      if (comments.value.length === 0) {
        return { label: 'Baru', color: 'red' }
      }
      const latestComment = comments.value[comments.value.length - 1]
      if (latestComment?.userId && latestComment.userId === currentUser.value?.id) {
        return { label: 'Menunggu', color: 'blue' }
      } else {
        return { label: 'Dijawab', color: 'pink' }
      }
    }

    const fetchData = async () => {
      const disposisiId = route.params.id

      if (!disposisiId) {
        notification.error({ message: 'ID atau tipe disposisi tidak valid' })
        router.push('/disposisi-masuk')
        return
      }

      loading.value = true
      try {
        let response
        response = await disposisiAPI.getPengurusById(disposisiId)

        if (response.data?.data) {
          data.value = response.data.data
        }

        // Fetch comments after getting disposisi data
        await fetchComments()
      } catch (error) {
        console.error('Error fetching disposisi:', error)
        notification.error({ message: 'Gagal memuat data disposisi' })
        data.value = null
      } finally {
        loading.value = false
      }
    }

    const scrollCommentsToBottom = () => {
      nextTick(() => {
        const commentsList = document.querySelector('.comments-list')
        if (commentsList) {
          commentsList.scrollTop = commentsList.scrollHeight
        }
      })
    }

    const fetchComments = async (shouldScroll = true, isPolling = false) => {
      const disposisiId = route.params.id

      // Only show loading state if not polling (polling should be silent/seamless)
      if (!isPolling) {
        commentsLoading.value = true
      }
      try {
        const previousLength = comments.value.length
        const response = await disposisiAPI.getComments('pengurus', disposisiId)

        // Handle the API response structure: response.data.data.comments
        const responseData = response.data?.data
        if (responseData && responseData.comments) {
          comments.value = responseData.comments
          statusDisposisi.value = responseData.statusDisposisi || null
        } else {
          comments.value = []
          statusDisposisi.value = null
        }

        // Only scroll if explicitly requested or if there are new comments
        if (shouldScroll || comments.value.length > previousLength) {
          scrollCommentsToBottom()
        }
      } catch (error) {
        console.error('Error fetching comments:', error)
        comments.value = []
      } finally {
        // Only hide loading state if we showed it
        if (!isPolling) {
          commentsLoading.value = false
        }
      }
    }

    const startPolling = () => {
      // Clear any existing interval
      if (pollingInterval.value) {
        clearInterval(pollingInterval.value)
      }

      // Poll every 10 seconds
      pollingInterval.value = setInterval(async () => {
        // Stop polling if statusDisposisi is 'Selesai Disposisi'
        if (statusDisposisi.value === 'Selesai Disposisi') {
          stopPolling()
          return
        }
        // Pass isPolling=true to fetch silently without loading state
        await fetchComments(false, true)
      }, 10000)
    }

    const stopPolling = () => {
      if (pollingInterval.value) {
        clearInterval(pollingInterval.value)
        pollingInterval.value = null
      }
    }

    const submitComment = async () => {
      const disposisiId = route.params.id

      if (!newComment.value.trim() && !commentFileList.value.length) {
        notification.warning({ message: 'Silakan isi komentar atau unggah file' })
        return
      }

      submittingComment.value = true
      try {
        let attachments = []

        // Separate surat keluar from user-uploaded files
        const suratKeluarFiles = commentFileList.value.filter(f => f.type === 'surat-keluar')
        const uploadedFiles = commentFileList.value.filter(f => f.type !== 'surat-keluar')

        // Upload user files if exist
        if (uploadedFiles.length > 0) {
          const formData = new FormData()
          // Append all files from the list
          uploadedFiles.forEach(fileItem => {
            const file = fileItem.originFileObj || fileItem
            formData.append('files', file)
          })
          const uploadResponse = await uploadFilesAPI.uploadFiles(formData)
          if (uploadResponse.data?.data?.uploaded) {
            // Add uploaded file paths as strings
            attachments = uploadResponse.data.data.uploaded.map(file => file.path)
          }
        }

        // Add surat keluar files as objects with name and suratKeluarId
        suratKeluarFiles.forEach(suratKeluar => {
          attachments.push({
            name: suratKeluar.name,
            suratKeluarId: suratKeluar.suratKeluarId,
          })
        })

        const commentData = {
          content: newComment.value.trim(),
          attachments: attachments,
        }

        await disposisiAPI.createComment('pengurus', disposisiId, commentData)

        // Reset form
        newComment.value = ''
        commentFileList.value = []
        selectedSuratKeluar.value = null

        // Refresh comments
        await fetchComments()
      } catch (error) {
        console.error('Error submitting comment:', error)
        notification.error({ message: 'Gagal mengirim komentar' })
      } finally {
        submittingComment.value = false
      }
    }

    // Surat Keluar Modal functions
    const openSuratKeluarModal = async () => {
      suratKeluarModalVisible.value = true
      suratKeluarLoading.value = true
      suratKeluarSearch.value = ''
      selectedSuratKeluarKeys.value = []
      try {
        const response = await suratKeluarAPI.getAll({ groupId: userGroupId })
        if (response.data?.data?.suratKeluar) {
          suratKeluarList.value = response.data.data.suratKeluar
        }
      } catch (error) {
        console.error('Error fetching surat keluar:', error)
        notification.error({ message: 'Gagal memuat daftar surat keluar' })
      } finally {
        suratKeluarLoading.value = false
      }
    }

    const filteredSuratKeluar = computed(() => {
      if (!suratKeluarSearch.value) {
        return suratKeluarList.value
      }
      const search = suratKeluarSearch.value.toLowerCase()
      return suratKeluarList.value.filter(
        surat =>
          (surat.nomorSurat || '').toLowerCase().includes(search) ||
          (surat.jenisSurat || '').toLowerCase().includes(search) ||
          (surat.perihal || '').toLowerCase().includes(search) ||
          (surat.namaKegiatan || '').toLowerCase().includes(search),
      )
    })

    const suratKeluarColumns = [
      {
        title: 'Nomor Surat',
        dataIndex: 'nomorSurat',
        key: 'nomorSurat',
        width: 140,
      },
      {
        title: 'Tanggal Surat',
        dataIndex: 'tanggalSurat',
        key: 'tanggalSurat',
        width: 130,
      },
      {
        title: 'Jenis Surat',
        dataIndex: 'jenisSurat',
        key: 'jenisSurat',
        width: 130,
      },
      {
        title: 'Perihal',
        dataIndex: 'namaKegiatan',
        key: 'namaKegiatan',
        width: 180,
      },
      {
        title: 'Pengaju',
        dataIndex: 'creator.name',
        key: 'pengaju',
        width: 120,
      },
    ]

    const onSuratKeluarSelectionChange = selectedRowKeys => {
      selectedSuratKeluarKeys.value = selectedRowKeys
    }

    const confirmSuratKeluarSelection = () => {
      // Add all selected surat keluar to commentFileList
      const selectedRecords = suratKeluarList.value.filter(surat =>
        selectedSuratKeluarKeys.value.includes(surat.id),
      )

      selectedRecords.forEach(record => {
        const suratFile = {
          uid: `surat-keluar-${record.id}`,
          name: `Surat Keluar - ${record.nomorSurat}`,
          suratKeluarId: record.id,
          type: 'surat-keluar',
        }
        // Avoid duplicates
        if (!commentFileList.value.some(f => f.suratKeluarId === record.id)) {
          commentFileList.value = [...commentFileList.value, suratFile]
        }
      })
      suratKeluarModalVisible.value = false
      selectedSuratKeluarKeys.value = []
    }

    const closeSuratKeluarModal = () => {
      suratKeluarModalVisible.value = false
      selectedSuratKeluarKeys.value = []
      suratKeluarSearch.value = ''
    }

    // Check if attachment is a surat keluar object
    const isSuratKeluarAttachment = attachment => {
      return typeof attachment === 'object' && attachment !== null && attachment.suratKeluarId
    }

    // Navigate to surat keluar detail page - opens PDF preview modal
    const navigateToSuratKeluar = async attachment => {
      try {
        // Set the title from attachment name
        pdfPreviewTitle.value = 'Preview Surat Keluar'

        // Handle pdfPaths array (new structure)
        if (attachment.pdfPaths && attachment.pdfPaths.length > 0) {
          if (attachment.pdfPaths.length === 1) {
            // Single PDF - use pdfPath prop
            pdfPreviewPath.value = attachment.pdfPaths[0]
            pdfPreviewList.value = []
          } else {
            // Multiple PDFs - use pdfList prop
            pdfPreviewPath.value = ''
            pdfPreviewList.value = attachment.pdfPaths.map((path, index) => ({
              path: path,
              name: `Dokumen ${index + 1}`,
            }))
          }
          pdfPreviewVisible.value = true
          return
        }

        // Handle single pdfPath (old structure)
        if (attachment.pdfPath) {
          pdfPreviewPath.value = attachment.pdfPath
          pdfPreviewList.value = []
          pdfPreviewVisible.value = true
          return
        }

        // If no path available, fetch from API
        if (attachment.suratKeluarId) {
          const response = await suratKeluarAPI.getById(attachment.suratKeluarId)
          const suratKeluar = response.data?.data || response.data
          pdfPreviewTitle.value = 'Preview Surat Keluar'
          pdfPreviewPath.value = suratKeluar.pdfPath || ''
          pdfPreviewList.value = []
          pdfPreviewVisible.value = true
        }
      } catch (error) {
        console.error('Error fetching surat keluar:', error)
        notification.error({
          message: 'Error',
          description: 'Gagal memuat surat keluar',
        })
      }
    }

    const previewSuratMasuk = () => {
      if (data.value?.suratMasuk?.pathToFile) {
        console.log('Previewing surat masuk:', data.value.suratMasuk.pathToFile)
        pdfPreviewTitle.value = 'Preview Surat Masuk'
        pdfPreviewPath.value = data.value.suratMasuk.pathToFile.replace(/^\/files/, '')
        pdfPreviewList.value = []
        pdfPreviewVisible.value = true
      }
    }

    const isCommentOwner = comment => {
      return comment?.userId && comment.userId === currentUser.value?.id
    }

    const getInitials = name => {
      if (!name) return '?'
      return name
        .split(' ')
        .map(word => word[0])
        .join('')
        .substring(0, 2)
        .toUpperCase()
    }

    const formatCommentTime = dateString => {
      if (!dateString) return ''
      return dayjs(dateString).format('DD MMM YYYY, HH:mm') + ` (${dayjs(dateString).fromNow()})`
    }

    const beforeUpload = file => {
      // Prevent auto upload, add to list
      commentFileList.value = [...commentFileList.value, file]
      return false
    }

    const removeFile = index => {
      commentFileList.value.splice(index, 1)
    }

    const handleRemoveFile = () => {
      commentFileList.value = []
    }

    const filterSuratKeluar = (input, option) => {
      const label = option.children?.[0]?.children || ''
      return label.toLowerCase().includes(input.toLowerCase())
    }

    const formatDate = dateString => {
      if (!dateString) return '-'
      return dayjs(dateString).format('DD MMMM YYYY')
    }

    const formatDateTime = dateString => {
      if (!dateString) return '-'
      return dayjs(dateString).format('dddd YYYY-MM-DD HH:mm')
    }

    const getFileUrl = path => {
      if (!path) return '#'
      return `${FILES_URL}${path}`
    }

    const getFileName = path => {
      if (!path) return 'File tidak tersedia'
      const parts = path.split('/')
      return parts[parts.length - 1]
    }

    const getSuratMasukNumber = () => {
      return data.value?.suratMasuk?.nomorSurat || '-'
    }

    const getDisposisiKepada = () => {
      if (!data.value?.disposisiKepadaUser || data.value.disposisiKepadaUser.length === 0) {
        return '-'
      }
      return data.value.disposisiKepadaUser.map(user => `${user.name} (${user.jabatan})`).join(', ')
    }

    const goBack = () => {
      router.push('/disposisi-masuk')
    }

    onMounted(async () => {
      await fetchData()
      startPolling()
      // Ensure scroll to bottom on initial load
      scrollCommentsToBottom()
    })

    onUnmounted(() => {
      stopPolling()
    })

    return {
      loading,
      data,
      comments,
      commentsLoading,
      newComment,
      submittingComment,
      activeTab,
      commentFileList,
      selectedSuratKeluar,
      suratKeluarList,
      statusDisposisi,
      // Surat Keluar Modal
      suratKeluarModalVisible,
      suratKeluarLoading,
      suratKeluarSearch,
      selectedSuratKeluarKeys,
      filteredSuratKeluar,
      suratKeluarColumns,
      // PDF Preview Modal
      pdfPreviewVisible,
      pdfPreviewTitle,
      pdfPreviewPath,
      pdfPreviewList,
      formatDate,
      formatDateTime,
      formatCommentTime,
      getFileUrl,
      getFileName,
      getSuratMasukNumber,
      getDisposisiKepada,
      getInitials,
      isCommentOwner,
      isSuratKeluarAttachment,
      navigateToSuratKeluar,
      previewSuratMasuk,
      goBack,
      submitComment,
      getSubstatus,
      beforeUpload,
      handleRemoveFile,
      removeFile,
      filterSuratKeluar,
      openSuratKeluarModal,
      onSuratKeluarSelectionChange,
      confirmSuratKeluarSelection,
      closeSuratKeluarModal,
    }
  },
}
</script>

<style lang="scss" scoped>
.detail-disposisi-page {
  min-height: calc(100vh - 64px);
  padding: 24px;

  @media (max-width: 768px) {
    padding: 0;
  }

  .page-header {
    margin-bottom: 24px;

    .back-button {
      padding: 0;
      margin-bottom: 8px;
      font-size: 14px;
      color: #e10f28;

      &:hover {
        color: #ff4d4f;
      }
    }

    .header-title-wrapper {
      margin-bottom: 12px;

      h1 {
        margin: 0;
        font-size: 24px;
        font-weight: 600;
        color: #262626;
      }

      .header-subtitle {
        margin: 4px 0 0;
        font-size: 16px;
        color: #595959;
      }
    }

    .header-tags {
      display: flex;
      gap: 8px;
    }
  }

  .loading-container,
  .error-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 400px;
    background: white;
    border-radius: 8px;
    padding: 48px 24px;
  }

  .content-container {
    background: white;
    border-radius: 8px;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

    @media (max-width: 768px) {
      padding: 16px;
    }

    .title-container {
      padding: 16px 18px;
      border-bottom: 1px solid #d9d9d9;
      display: flex;
      align-items: center;
    }

    .section-title {
      font-size: 24px;
      font-weight: 600;
      color: #282828;

      @media (max-width: 768px) {
        font-size: 16px;
      }

      &:first-child {
        margin-top: 0;
      }
    }

    .sub-section-title {
      font-size: 18px;
      font-weight: 600;
      color: #282828;

      @media (max-width: 768px) {
        font-size: 16px;
      }
    }

    .detail-grid {
      display: flex;
      flex-direction: column;
      gap: 12px;
      margin-top: 12px;

      .detail-row {
        display: flex;
        align-items: flex-start;
        padding: 4px 0;

        @media (max-width: 768px) {
          flex-direction: column;
        }

        &:last-child {
          border-bottom: none;
        }

        .detail-label {
          width: 200px;
          min-width: 200px;
          font-weight: 700;
          color: #595959;
        }

        .detail-value {
          flex: 1;
          color: #262626;

          .file-link {
            color: #e10f28;
            text-decoration: none;
            display: inline-flex;
            align-items: center;
            gap: 4px;

            @media (max-width: 768px) {
              white-space: normal;
              flex-wrap: wrap;
              word-break: break-word;
            }

            .file-link-name {
              flex: 1;
              word-break: break-word;
            }

            &:hover {
              text-decoration: underline;
            }
          }
        }
      }
    }

    // Comment Section Styles
    .comment-section {
      padding: 24px;

      @media (max-width: 768px) {
        padding: 16px;
      }

      .comment-empty-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 48px 12px;
        text-align: center;
        color: #8c8c8c;
        border-radius: 8px;
        margin-bottom: 24px;

        .empty-icon {
          font-size: 48px;
          margin-bottom: 16px;
          color: #bfbfbf;
        }

        p {
          max-width: 750px;
          margin: 0;
          line-height: 1.6;
        }
      }

      .comments-list {
        margin-bottom: 24px;
        height: 400px;
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        gap: 16px;

        .comment-item {
          display: flex;
          flex-direction: column;
          align-items: flex-start;

          &.own-comment {
            align-items: flex-end;

            .comment-bubble {
              background: #f2f4f8;
            }
          }

          .comment-bubble {
            background: #efefff;
            border-radius: 12px;
            padding: 12px 16px;
            width: 90%;

            .comment-time {
              font-size: 12px;
              color: #8c8c8c;
              margin-top: 4px;
              padding: 0 4px;
            }

            .comment-user-name {
              font-weight: 600;
              font-size: 13px;
              color: #262626;
              margin-bottom: 8px;
              display: flex;
              justify-content: space-between;

              @media (max-width: 430px) {
                flex-direction: column;
                align-items: flex-start;
                gap: 4px;
              }

              .comment-time {
                font-size: 12px;
                color: #798892;
                margin-top: 4px;
                padding: 0 4px;
                font-weight: 400;

                @media (max-width: 430px) {
                  padding: 0;
                }
              }
            }

            .comment-content {
              p {
                margin: 0;
                color: #262626;
                font-size: 14px;
                line-height: 1.5;
              }

              .comment-attachment,
              .comment-surat-keluar {
                margin-bottom: 8px;

                .attachment-link,
                .surat-keluar-link {
                  display: inline-flex;
                  align-items: center;
                  gap: 6px;
                  color: #321e8d;
                  font-size: 14px;
                  text-decoration: none;

                  .export-icon {
                    color: #e10f28;
                  }

                  .attachment-icon {
                    color: rgba(0, 0, 0, 0.45);
                  }
                }
              }
            }
          }
        }
      }

      .comment-input-section {
        border-radius: 8px;
        overflow: hidden;

        .comment-action-button {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 12px;
          background: transparent;
          border: 2px solid transparent;
          border-radius: 8px;
          cursor: pointer;
          font-size: 12px;
          font-weight: 700;
          color: #282828;
          transition: all 0.2s ease;

          &:hover {
            border-color: #d9d9d9;
            color: #262626;
          }

          &:disabled {
            color: #98a2b3;

            &:hover {
              border-color: transparent;
              cursor: not-allowed;
            }
          }
        }

        .comment-tabs {
          display: flex;
          gap: 8px;
          padding: 12px 0;
          border-top: 1px solid #d9d9d9;
          flex-wrap: wrap;

          :deep(.ant-btn) {
            border-radius: 4px;
            font-size: 12px;
            font-weight: 600;

            &.ant-btn-primary {
              background: transparent;
              color: #e10f28;
              border-color: #e10f28;
            }
          }
        }

        .upload-section,
        .surat-keluar-section {
          padding: 16px;
          border-bottom: 1px solid #f0f0f0;
        }

        .upload-button-wrapper {
          display: inline-block;
        }

        .attachment-preview {
          border-radius: 6px;
          margin-bottom: 12px;

          .attachment-item {
            display: flex;
            align-items: center;
            padding: 4px 12px;
            background: white;
            border-radius: 4px;
            width: 50%;
            gap: 8px;

            &:hover {
              background: #fafafa;
            }

            .file-icon {
              font-size: 14px;
              color: rgba(0, 0, 0, 0.45);
            }

            .attachment-name {
              flex: 1;
              font-size: 14px;
              color: #321e8d;
              word-break: break-word;
            }

            .delete-button {
              background: none;
              border: none;
              cursor: pointer;
              color: #ff4d4f;
              padding: 4px;
              display: flex;
              align-items: center;
              justify-content: center;
              border-radius: 2px;
              transition: all 0.2s;

              &:hover {
                color: #ff7875;
                background: #fff1f0;
              }

              svg {
                width: 16px;
                height: 16px;
              }
            }
          }
        }

        .comment-textarea-wrapper {
          padding: 0 0 0 0;
          .custom-comment-input-row {
            display: flex;
            align-items: flex-end;
            gap: 12px;
            width: 100%;

            @media (max-width: 768px) {
              flex-direction: column;
              align-items: stretch;
            }
          }
          .custom-comment-textarea {
            border: 1px solid #d9d9d9;
            border-radius: 10px;
            resize: none;
            min-height: 48px;
            font-size: 14px;
            flex: 1 1 auto;
            transition: box-shadow 0.2s;
            box-shadow: none;
            padding: 12px 16px;
          }
          .custom-send-btn {
            background: #e10f28;
            border: none;
            color: #fff;
            border-radius: 50px;
            min-width: 80px;
            height: 36px;
            font-weight: 600;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            transition: background 0.2s;
            box-shadow: none;
          }
          .custom-send-btn:disabled {
            background: #d9d9d9;
            color: #fff;
          }
          .custom-send-btn:not(:disabled):hover {
            background: #f3374d;
            color: #fff;
          }
          .send-label {
            font-size: 13px;
            font-weight: 600;
            letter-spacing: 0.5px;
            margin: 0;
          }
        }
      }
    }
  }
}

.comment {
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  margin-top: 18px;
}

.view-icon {
  color: #e10f28;
}

::v-deep(.ant-upload.ant-upload-drag) {
  padding: 0;
  background: #fff;
  border: none;

  span {
    padding: 0 !important;
  }
}

::v-deep(.ant-upload-drag-container) {
  display: flex !important;
  gap: 8px !important;
  align-items: center !important;
}

::v-deep(.ant-upload) {
  font-size: 12px !important;
}

::v-deep(.ant-tag) {
  font-weight: 700;
  border-radius: 50px;
}

.view-icon-link {
  cursor: pointer;
  display: inline-flex;
  align-items: center;

  &:hover {
    opacity: 0.8;
  }
}

// Surat Keluar Modal Styles
.surat-keluar-modal {
  .modal-subtitle {
    font-size: 14px;
    color: #595959;
    margin-bottom: 16px;
  }

  .surat-keluar-search {
    margin-bottom: 16px;

    :deep(.ant-input) {
      border-radius: 8px;
    }
  }

  .surat-keluar-table-container {
    max-height: 450px;
    overflow: hidden;
    margin-bottom: 16px;
    border: 1px solid #f0f0f0;
    border-radius: 8px;

    .surat-nomor {
      font-weight: 600;
      color: #321e8d;
    }

    :deep(.ant-spin-container) {
      display: flex;
      flex-direction: column;
      height: 100%;
    }

    :deep(.ant-table-wrapper) {
      display: flex;
      flex-direction: column;

      .ant-table {
        font-size: 13px;
        flex: 1;
      }

      .ant-table-head {
        overflow: hidden !important;

        th {
          padding: 8px 12px;
          font-weight: 600;
          background: #fafafa;
        }
      }

      .ant-table-body {
        overflow-x: auto !important;
        overflow-y: auto !important;

        td {
          padding: 8px 12px;
        }
      }
    }
  }

  .surat-keluar-modal-actions {
    display: flex;
    gap: 12px;
    padding-top: 16px;
    border-top: 1px solid #f0f0f0;
  }
}

.confirm-button {
  border-radius: 50px;
  font-size: 14px;
  font-weight: 700;
  border: 2px solid #e10f28;
  width: 50%;
  max-width: 230px;
  background: #e10f28;
  color: white;
  padding: 6px 12px;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: white !important;
    border-color: #e10f28 !important;
    color: #e10f28 !important;
  }

  &:disabled {
    border-color: #98a2b3;
    background: #98a2b3;
    cursor: not-allowed;
  }
}

.remove-cancel-button {
  border-radius: 50px;
  padding: 6px 24px;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.3s;
}

.remove-cancel-secondary {
  background: #ffffff;
  border: 2px solid #1a1a1a;
  color: #1a1a1a;

  &:hover {
    background: #1a1a1a;
    border-color: white;
    color: white;
  }
}

::v-deep(.ant-checkbox-inner) {
  border-radius: 4px !important;
}

::v-deep(.ant-checkbox-checked .ant-checkbox-inner) {
  background-color: #e10f28 !important;
  border-color: #e10f28 !important;
}

::v-deep(.ant-checkbox-indeterminate .ant-checkbox-inner::after) {
  background-color: transparent !important;
}

::v-deep(.ant-checkbox-indeterminate .ant-checkbox-inner) {
  background-color: transparent !important;
  border-color: #d9d9d9 !important;
}
</style>
